import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { GradientBorder } from '@/components/ui/gradient-border';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex origin-center items-center justify-center whitespace-nowrap transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-(--duration-fast) ease-standard motion-reduce:transition-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none aria-busy:cursor-progress",
  {
    variants: {
      appearance: {
        solid: 'hover:opacity-90 active:opacity-80',
        gradient: 'bg-linear-[113deg] hover:opacity-80 active:opacity-70',
        soft: 'hover:opacity-90 active:opacity-80',
        outline: 'bg-background shadow-xs',
        ghost: 'bg-transparent hover:bg-accent active:bg-accent',
        link: 'border-none bg-transparent p-0 h-auto underline-offset-4 hover:underline focus-visible:ring-0 focus-visible:border-transparent',
        ai: 'bg-transparent border-0',
      },
      variant: {
        primary: '',
        secondary: '',
        info: '',
        success: '',
        warning: '',
        destructive: '',
      },
      size: {
        default:
          'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2',
        xs: "h-[26px] gap-1 px-2 text-xs has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 px-3 has-data-[icon=inline-end]:pe-2.5 has-data-[icon=inline-start]:ps-2.5',
        md: 'h-9 gap-2 px-4 has-data-[icon=inline-end]:pe-3.5 has-data-[icon=inline-start]:ps-3.5',
        lg: 'h-10 gap-2 px-6',
        xl: "h-12 gap-2 px-4.5 text-base has-data-[icon=inline-end]:pe-4 has-data-[icon=inline-start]:ps-4 [&_svg:not([class*='size-'])]:size-5",
        '2xl':
          "h-14 gap-2.5 px-5 text-lg has-data-[icon=inline-end]:pe-4.5 has-data-[icon=inline-start]:ps-4.5 [&_svg:not([class*='size-'])]:size-6",
        icon: 'size-9',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
        'icon-xl': "size-12 [&_svg:not([class*='size-'])]:size-5",
        'icon-2xl': "size-14 [&_svg:not([class*='size-'])]:size-6",
      },
      shape: {
        rounded: 'rounded-xl',
        round: 'rounded-full',
      },
    },
    compoundVariants: [
      // ===== SOLID =====
      {
        appearance: 'solid',
        variant: 'primary',
        className: 'bg-primary text-primary-foreground',
      },
      {
        appearance: 'solid',
        variant: 'secondary',
        className:
          'bg-secondary text-secondary-foreground aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
      },
      {
        appearance: 'solid',
        variant: 'info',
        className: 'bg-info text-info-foreground',
      },
      {
        appearance: 'solid',
        variant: 'success',
        className: 'bg-success text-success-foreground',
      },
      {
        appearance: 'solid',
        variant: 'warning',
        className: 'bg-warning text-warning-foreground',
      },
      {
        appearance: 'solid',
        variant: 'destructive',
        className: 'bg-destructive text-destructive-foreground',
      },

      // ===== GRADIENT =====
      {
        appearance: 'gradient',
        variant: 'primary',
        className:
          'from-custom-tw-gradient-from to-custom-tw-gradient-to text-primary-foreground hover:from-custom-tw-gradient-to hover:to-custom-tw-gradient-from active:from-custom-tw-gradient-to active:to-custom-tw-gradient-from',
      },
      {
        appearance: 'gradient',
        variant: 'secondary',
        className:
          'from-secondary to-secondary/60 text-secondary-foreground hover:from-secondary/60 hover:to-secondary active:from-secondary/60 active:to-secondary',
      },
      {
        appearance: 'gradient',
        variant: 'info',
        className:
          'from-info to-info/60 text-info-foreground hover:from-info/60 hover:to-info active:from-info/60 active:to-info',
      },
      {
        appearance: 'gradient',
        variant: 'success',
        className:
          'from-success to-success/60 text-success-foreground hover:from-success/60 hover:to-success active:from-success/60 active:to-success',
      },
      {
        appearance: 'gradient',
        variant: 'warning',
        className:
          'from-warning to-warning/60 text-warning-foreground hover:from-warning/60 hover:to-warning active:from-warning/60 active:to-warning',
      },
      {
        appearance: 'gradient',
        variant: 'destructive',
        className:
          'from-destructive to-destructive/60 text-destructive-foreground hover:from-destructive/60 hover:to-destructive active:from-destructive/60 active:to-destructive',
      },

      // ===== SOFT =====
      {
        appearance: 'soft',
        variant: 'primary',
        className:
          'bg-primary/10 text-primary hover:bg-primary/30 active:bg-primary/10 dark:bg-primary/30 dark:text-primary dark:hover:bg-primary/50 dark:active:bg-primary/30',
      },
      {
        appearance: 'soft',
        variant: 'secondary',
        className:
          'bg-secondary/10 text-secondary-foreground hover:bg-secondary/30 active:bg-secondary/10',
      },
      {
        appearance: 'soft',
        variant: 'info',
        className: 'bg-info/10 text-info hover:bg-info/30 active:bg-info/10',
      },
      {
        appearance: 'soft',
        variant: 'success',
        className:
          'bg-success/10 text-success hover:bg-success/30 active:bg-success/10',
      },
      {
        appearance: 'soft',
        variant: 'warning',
        className:
          'bg-warning/10 text-warning hover:bg-warning/30 active:bg-warning/10',
      },
      {
        appearance: 'soft',
        variant: 'destructive',
        className:
          'bg-destructive/10 hover:bg-destructive/30 active:bg-destructive/10 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30 dark:active:bg-destructive/20',
      },

      // ===== OUTLINE =====
      {
        appearance: 'outline',
        className:
          'bg-card border-border backdrop-blur-[4px] shadow-none supports-backdrop-filter:backdrop-blur-[4px] hover:border-border-prominent active:border-border-prominent',
      },
      {
        appearance: 'outline',
        shape: 'rounded',
        className: 'rounded-xl',
      },
      {
        appearance: 'outline',
        size: 'xs',
        className: 'h-[26px] gap-1 px-2',
      },
      {
        appearance: 'outline',
        size: 'sm',
        className: 'h-8 gap-1.5 px-2.5',
      },
      {
        appearance: 'outline',
        size: 'default',
        className: 'h-9 gap-2 px-4',
      },
      {
        appearance: 'outline',
        size: 'md',
        className: 'h-10 gap-2 px-4',
      },
      {
        appearance: 'outline',
        size: 'lg',
        className: 'h-10 gap-2 px-6',
      },
      {
        appearance: 'outline',
        variant: 'primary',
        className: 'border-border text-foreground',
      },
      {
        appearance: 'outline',
        variant: 'secondary',
        className:
          'border-border text-secondary-foreground aria-expanded:bg-muted/50 aria-expanded:text-foreground',
      },
      {
        appearance: 'outline',
        variant: 'info',
        className: 'border-border text-info',
      },
      {
        appearance: 'outline',
        variant: 'success',
        className: 'border-border text-success',
      },
      {
        appearance: 'outline',
        variant: 'warning',
        className: 'border-border text-warning',
      },
      {
        appearance: 'outline',
        variant: 'destructive',
        className: 'border-border text-destructive',
      },

      // ===== GHOST =====
      {
        appearance: 'ghost',
        variant: 'primary',
        className: 'text-primary',
      },
      {
        appearance: 'ghost',
        variant: 'secondary',
        className:
          'hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
      },
      {
        appearance: 'ghost',
        variant: 'info',
        className: 'text-info',
      },
      {
        appearance: 'ghost',
        variant: 'success',
        className: 'text-success',
      },
      {
        appearance: 'ghost',
        variant: 'warning',
        className: 'text-warning',
      },
      {
        appearance: 'ghost',
        variant: 'destructive',
        className: 'text-destructive',
      },

      // ===== AI (inner button styling — GradientBorder wraps externally) =====
      {
        appearance: 'ai',
        variant: 'primary',
        className: 'text-primary hover:bg-primary/10',
      },
      {
        appearance: 'ai',
        variant: 'secondary',
        className: 'text-secondary-foreground hover:bg-muted',
      },

      // ===== LINK =====
      { appearance: 'link', variant: 'primary', className: 'text-primary' },
      {
        appearance: 'link',
        variant: 'secondary',
        className: 'text-secondary-foreground',
      },
      { appearance: 'link', variant: 'info', className: 'text-info' },
      { appearance: 'link', variant: 'success', className: 'text-success' },
      { appearance: 'link', variant: 'warning', className: 'text-warning' },
      {
        appearance: 'link',
        variant: 'destructive',
        className: 'text-destructive hover:text-destructive/80',
      },
    ],
    defaultVariants: {
      appearance: 'solid',
      variant: 'primary',
      size: 'default',
      shape: 'rounded',
    },
  },
);

type DotColor = 'success' | 'error' | 'warning' | 'brand' | 'gray';

type ButtonAppearance =
  | 'solid'
  | 'gradient'
  | 'soft'
  | 'outline'
  | 'ghost'
  | 'link'
  | 'ai';
type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'destructive';
type ButtonShape = 'rounded' | 'round';

/**
 * Render prop type for polymorphic rendering.
 * Accepts either a React element or a function that receives props and returns an element.
 * @template Props The props to be passed to the rendered element
 */
type RenderProp<Props> =
  | React.ReactElement
  | ((props: Props) => React.ReactElement);

export interface ButtonProps
  extends React.ComponentProps<'button'>,
    Pick<VariantProps<typeof buttonVariants>, 'size'> {
  /** Visual appearance - how the button renders */
  appearance?: ButtonAppearance;
  /** Semantic variant - what the button means */
  variant?: ButtonVariant;
  /** Shape of the button corners */
  shape?: ButtonShape;
  /** Shows a loading spinner and disables the button */
  loading?: boolean;
  /** Set to false when render targets a non-button element. */
  nativeButton?: boolean;
  /** Icon displayed at the start (inline-start) of the button text - respects RTL */
  startIcon?: React.ReactNode;
  /** Icon displayed at the end (inline-end) of the button text - respects RTL */
  endIcon?: React.ReactNode;
  /** Shows a colored dot indicator before the button text */
  dotIndicator?: boolean;
  /** Color of the dot indicator */
  dotColor?: DotColor;
  /** Badge content displayed after the button label. Text badges are styled from the button appearance and variant. */
  badge?: React.ReactNode;
  /** For icon-only buttons - renders just the icon without text wrapper */
  iconOnly?: boolean;
  /** Enables animated gradient border (only for appearance="ai"). Auto-true when loading. */
  animated?: boolean;
  /**
   * Render prop for polymorphic rendering. Accepts either a React element or a render function.
   * Use this to render the button as a different element (e.g., link) while preserving button styles.
   * Note: When render is provided, loading/startIcon/endIcon/dotIndicator/badge are ignored.
   */
  render?: RenderProp<React.ComponentProps<'button'>>;
}

const dotColorClasses: Record<DotColor, string> = {
  success: 'bg-success',
  error: 'bg-destructive',
  warning: 'bg-warning',
  brand: 'bg-primary',
  gray: 'bg-muted',
};

const devWarnings = new Set<string>();

/** Icon-only size variants for type-safe detection */
const ICON_SIZES = [
  'icon',
  'icon-xs',
  'icon-sm',
  'icon-lg',
  'icon-xl',
  'icon-2xl',
] as const;

const buttonBadgeShapeClasses: Record<ButtonShape, string> = {
  round: 'rounded-full',
  rounded: 'rounded-sm',
};

const buttonBadgeWhiteColorClasses: Record<ButtonVariant, string> = {
  primary: 'text-primary',
  secondary: 'text-secondary-foreground',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

const buttonBadgeSoftColorClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-soft-primary text-soft-primary-foreground hover:bg-opacity-primary-20 dark:bg-soft-primary dark:text-soft-primary-foreground',
  secondary:
    'bg-soft-secondary text-soft-secondary-foreground hover:bg-opacity-secondary-20 dark:bg-soft-secondary dark:text-soft-secondary-foreground',
  info: 'bg-soft-info text-soft-info-foreground hover:bg-opacity-info-20 dark:bg-soft-info dark:text-soft-info-foreground',
  success:
    'bg-soft-success text-soft-success-foreground hover:bg-opacity-success-20 dark:bg-soft-success dark:text-soft-success-foreground',
  warning:
    'bg-soft-warning text-soft-warning-foreground hover:bg-opacity-warning-20 dark:bg-soft-warning dark:text-soft-warning-foreground',
  destructive:
    'bg-soft-destructive text-soft-destructive-foreground hover:bg-opacity-destructive-20 dark:bg-soft-destructive dark:text-soft-destructive-foreground',
};

/** Delay feedback for short operations; reverse immediately, without dwell. */
const PENDING_FADE =
  'transition-[opacity,visibility] duration-(--duration-fast) ease-standard delay-(--motion-duration-base) motion-reduce:transition-[visibility] motion-reduce:duration-0';

/**
 * Pending indicator. Always decorative: the button keeps its original
 * accessible name and announces pending state through `aria-busy`. It is
 * mounted permanently and crossfaded so reversal never leaves a stale node;
 * it only spins while pending.
 */
function PendingSpinner({
  pending,
  className,
}: {
  pending: boolean;
  className?: string;
}) {
  return (
    <Spinner
      role="presentation"
      aria-label={undefined}
      aria-hidden="true"
      data-pending={pending ? 'true' : 'false'}
      className={cn(
        'pointer-events-none absolute inset-0 m-auto size-full',
        pending
          ? cn(
              PENDING_FADE,
              'visible opacity-100 starting:invisible starting:opacity-0',
            )
          : 'transition-none animate-none invisible opacity-0',
        className,
      )}
    />
  );
}

/** Holds content and spinner in one box so the swap never changes layout. */
function PendingSlot({
  pending,
  slot,
  children,
}: {
  pending: boolean;
  slot: 'start' | 'end' | 'icon';
  children: React.ReactNode;
}) {
  return (
    <span
      data-slot="button-pending-slot"
      data-pending-slot={slot}
      className="relative inline-flex shrink-0 items-center justify-center min-h-[1em] min-w-[1em]"
    >
      <span
        className={cn(
          'inline-flex items-center justify-center',
          pending
            ? cn(
                PENDING_FADE,
                'invisible opacity-0 starting:visible starting:opacity-100 motion-reduce:opacity-100',
              )
            : 'transition-none visible opacity-100',
        )}
      >
        {children}
      </span>
      <PendingSpinner pending={pending} />
    </span>
  );
}

const Dot = ({
  color = 'success',
  className,
}: {
  color?: DotColor;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={cn(
      'size-2 rounded-full shrink-0',
      dotColorClasses[color],
      className,
    )}
  />
);

function ButtonBadge({
  appearance,
  badge,
  shape,
  variant,
}: {
  appearance: ButtonAppearance;
  badge: React.ReactNode;
  shape: ButtonShape;
  variant: ButtonVariant;
}) {
  if (React.isValidElement(badge)) {
    return badge;
  }

  if (badge === true || badge === false || badge == null) {
    return null;
  }

  const badgeVariant =
    (appearance === 'solid' || appearance === 'gradient') &&
    variant !== 'secondary'
      ? 'white'
      : 'soft';

  return (
    <span
      data-slot="badge"
      className={cn(
        'inline-flex h-5 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-2 py-0.5 text-xs font-medium transition-colors [&>svg]:size-3 [&>svg]:shrink-0 [&>svg]:pointer-events-none',
        buttonBadgeShapeClasses[shape],
        badgeVariant === 'white'
          ? 'bg-opacity-white-80 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current/50'
          : buttonBadgeSoftColorClasses[variant],
        badgeVariant === 'white' && buttonBadgeWhiteColorClasses[variant],
      )}
    >
      {badge}
    </span>
  );
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      loading = false,
      disabled,
      startIcon,
      endIcon,
      dotIndicator = false,
      dotColor = 'success',
      badge,
      iconOnly = false,
      animated,
      render,
      className,
      appearance = 'solid',
      variant = 'primary',
      shape = 'rounded',
      size = 'default',
      ...props
    },
    ref,
  ) => {
    // Dev-mode warning for icon-only buttons missing accessible label (once)
    if (
      process.env.NODE_ENV !== 'production' &&
      !devWarnings.has('icon-a11y')
    ) {
      const hasIconSize = ICON_SIZES.includes(
        size as (typeof ICON_SIZES)[number],
      );
      if (
        (iconOnly || hasIconSize) &&
        !props['aria-label'] &&
        !props['aria-labelledby']
      ) {
        devWarnings.add('icon-a11y');
        console.warn(
          'Button: Icon-only buttons require an `aria-label` or `aria-labelledby` for accessibility.',
        );
      }
    }

    const isAi = appearance === 'ai';
    const isAnimated = isAi ? (animated ?? loading) : false;

    const buttonClassName = cn(
      buttonVariants({ appearance, variant, shape, size }),
      className,
    );

    // When render prop is provided, pass through to primitive
    // (ignores loading/startIcon/endIcon/dotIndicator/badge)
    if (render) {
      return (
        <ButtonPrimitive
          ref={ref}
          data-slot="button"
          className={buttonClassName}
          render={render}
          disabled={disabled}
          {...props}
        >
          {children}
        </ButtonPrimitive>
      );
    }

    // Determine if this is an icon-only button based on size or explicit prop
    const isIconOnly =
      iconOnly || ICON_SIZES.includes(size as (typeof ICON_SIZES)[number]);
    const buttonBadge = !isIconOnly ? (
      <ButtonBadge
        appearance={appearance}
        badge={badge}
        shape={shape}
        variant={variant}
      />
    ) : null;

    // Icon-only: spinner crossfades over the icon in the same box.
    // With a start/end icon: the label stays visible and the spinner replaces
    // that icon in place. Plain text keeps its original intrinsic footprint and
    // visible label; aria-busy and native disabled convey pending without a spinner.
    let pendingSlot: 'start' | 'end' | null = null;
    if (!isIconOnly) {
      if (startIcon != null) pendingSlot = 'start';
      else if (endIcon != null) pendingSlot = 'end';
    }

    const pendingProps = {
      disabled: disabled || loading,
      'aria-busy': loading ? true : props['aria-busy'],
    } as const;

    const buttonElement = isIconOnly ? (
      <ButtonPrimitive
        ref={isAi ? undefined : ref}
        data-slot="button"
        className={buttonClassName}
        {...props}
        {...pendingProps}
      >
        <PendingSlot pending={loading} slot="icon">
          {children}
        </PendingSlot>
      </ButtonPrimitive>
    ) : (
      <ButtonPrimitive
        ref={isAi ? undefined : ref}
        data-slot="button"
        className={buttonClassName}
        {...props}
        {...pendingProps}
      >
        <span className="flex items-center gap-2">
          {dotIndicator && <Dot color={dotColor} />}
          {pendingSlot === 'start' ? (
            <PendingSlot pending={loading} slot="start">
              {startIcon}
            </PendingSlot>
          ) : (
            startIcon
          )}
          {children}
          {pendingSlot === 'end' ? (
            <PendingSlot pending={loading} slot="end">
              {endIcon}
            </PendingSlot>
          ) : (
            endIcon
          )}
          {badge != null && badge !== false && badge !== true && ' '}
          {buttonBadge}
        </span>
      </ButtonPrimitive>
    );

    if (isAi) {
      return (
        <GradientBorder
          ref={ref as React.Ref<HTMLDivElement>}
          animated={isAnimated}
          active
          disabled={disabled}
          className="w-auto inline-flex motion-reduce:animate-none"
          innerClassName="flex"
        >
          {buttonElement}
        </GradientBorder>
      );
    }

    return buttonElement;
  },
);

Button.displayName = 'Button';

/**
 * Clear/Close icon button - a ghost button specifically for close/clear actions
 * Commonly used in tags, dialogs, and dismissible elements
 */
export interface ClearButtonProps
  extends Omit<
    ButtonProps,
    | 'appearance'
    | 'variant'
    | 'children'
    | 'startIcon'
    | 'endIcon'
    | 'dotIndicator'
  > {
  /** Size of the clear button */
  size?: 'icon-xs' | 'icon-sm' | 'icon' | 'icon-lg' | 'icon-xl' | 'icon-2xl';
}

function ClearButton({
  className,
  size = 'icon-sm',
  ...props
}: ClearButtonProps) {
  return (
    <Button
      appearance="ghost"
      variant="secondary"
      size={size}
      iconOnly
      className={cn(
        'text-muted-foreground hover:text-secondary-foreground',
        className,
      )}
      aria-label="Clear"
      {...props}
    >
      <X />
    </Button>
  );
}

ClearButton.displayName = 'ClearButton';

export type { ButtonAppearance, ButtonShape, ButtonVariant };
export { Button, buttonVariants, ClearButton, Dot };
